/**
 * VersionRulesPanel.js
 *
 * Integrated rules sidebar for template version editing.
 *
 * Design: Flat, part of sidebar flow (not separate panels)
 * Auto-save: Debounced updates via PATCH
 *
 * Main Contract: States, addenda, package order
 * Early Pay: Number of options only
 * Disclosure: Assigned state only
 * Others: No UI shown
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { COLORS, SPACING, RADIUS } from '../designSystem';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const VALID_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const ADDENDUM_OPTIONS = [
  { key: 'requires_weekly_addendum', label: 'Weekly Addendum' },
  { key: 'requires_early_pay', label: 'Early Pay Addendum' },
  { key: 'requires_refi_payoff', label: 'Refi / Third Party Addendum' },
  { key: 'requires_additional_entity', label: 'Additional Entity Addendum' },
  { key: 'requires_misc_addenda', label: 'Misc Addenda' },
];

/**
 * Main wrapper - dispatches to type-specific UI
 */
export default function VersionRulesPanel({ templateId, versionId, templateType, token, authoring, onRulesSaved }) {
  if (templateType === 'main_contract') {
    return <MainContractRules templateId={templateId} versionId={versionId} token={token} authoring={authoring} onRulesSaved={onRulesSaved} />;
  }

  if (templateType === 'early_pay_addendum') {
    return <EarlyPayRules templateId={templateId} versionId={versionId} token={token} authoring={authoring} onRulesSaved={onRulesSaved} />;
  }

  if (templateType === 'state_disclosure') {
    return <DisclosureRules templateId={templateId} versionId={versionId} token={token} authoring={authoring} onRulesSaved={onRulesSaved} />;
  }

  return null;
}

/**
 * Main Contract Rules - Full integration
 */
function MainContractRules({ templateId, versionId, token, authoring, onRulesSaved }) {
  const [rules, setRules] = useState({});
  const [statesExpanded, setStatesExpanded] = useState(false);
  const [addendaExpanded, setAddendaExpanded] = useState(true);
  const [packageOrderExpanded, setPackageOrderExpanded] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  const [saveError, setSaveError] = useState(null);
  const autoSaveRef = useRef(null);

  // Load rules from authoring
  useEffect(() => {
    if (authoring?.applicability_rules_json) {
      setRules(authoring.applicability_rules_json);
    }
  }, [authoring]);

  // Auto-save with debounce
  const autoSave = useCallback((updatedRules) => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);

    setSaveStatus('saving');
    setSaveError(null);

    autoSaveRef.current = setTimeout(async () => {
      try {
        console.log('📝 [AUTO-SAVE] Saving rules:', updatedRules);
        const response = await axios.patch(
          `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
          { applicability_rules_json: updatedRules },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ [AUTO-SAVE] Rules saved successfully');
        console.log('📋 [AUTO-SAVE] Response status:', response.status);
        console.log('📋 [AUTO-SAVE] Response data:', response.data);
        console.log('📋 [AUTO-SAVE] Saved rules in response:', response.data?.applicability_rules_json);
        setSaveStatus('saved');

        // Update parent component's authoring state so changes persist on navigation
        if (onRulesSaved) {
          console.log('🔄 [AUTO-SAVE] Syncing with parent component');
          onRulesSaved(updatedRules);
        }

        setTimeout(() => setSaveStatus(null), 2000); // Clear indicator after 2 seconds
      } catch (e) {
        console.error('❌ [AUTO-SAVE] Failed:', e.response?.data || e.message);
        console.error('❌ [AUTO-SAVE] Full error:', e);
        setSaveStatus('error');
        setSaveError(e.response?.data?.detail || e.message);
      }
    }, 400);
  }, [templateId, versionId, token, onRulesSaved]);

  // State toggles
  const handleStateToggle = (state) => {
    const include = rules.states_include || [];
    const updated = include.includes(state)
      ? include.filter(s => s !== state)
      : [...include, state];
    const newRules = { ...rules, states_include: updated };
    setRules(newRules);
    autoSave(newRules);
  };

  const handleSelectAllStates = () => {
    const newRules = { ...rules, states_include: VALID_STATES };
    setRules(newRules);
    autoSave(newRules);
  };

  const handleClearAllStates = () => {
    const newRules = { ...rules, states_include: [] };
    setRules(newRules);
    autoSave(newRules);
  };

  // Addendum toggles
  const handleAddendumToggle = (key) => {
    const newRules = { ...rules, [key]: !(rules[key] !== false) };
    setRules(newRules);
    autoSave(newRules);
  };

  // Package order drag handlers
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetItem) return;

    const currentOrder = buildPackageOrder(rules);
    const draggedIndex = currentOrder.indexOf(draggedItem);
    const targetIndex = currentOrder.indexOf(targetItem);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    const newRules = { ...rules, package_order: newOrder };
    setRules(newRules);
    autoSave(newRules);
    setDraggedItem(null);
  };

  // Build package order
  const buildPackageOrder = (rulesData) => {
    const order = rulesData.package_order || [];
    const items = ['main_contract', 'state_disclosure'];

    if (rulesData.requires_weekly_addendum !== false) items.push('weekly_addendum');
    if (rulesData.requires_early_pay !== false) items.push('early_pay_addendum');
    if (rulesData.requires_refi_payoff !== false) items.push('refi_third_party_payoff_page');
    if (rulesData.requires_additional_entity !== false) items.push('additional_entity_addendum');
    if (rulesData.requires_misc_addenda !== false) items.push('misc');

    return order.length === items.length ? order : items;
  };

  const packageOrder = buildPackageOrder(rules);
  const stateCount = (rules.states_include || []).length;

  return (
    <div style={styles.container}>
      {/* Save Status Indicator */}
      {saveStatus && (
        <div style={{
          ...styles.saveIndicator,
          backgroundColor: saveStatus === 'saving' ? '#FFA500' :
                          saveStatus === 'saved' ? '#4DBD94' : '#D32F2F',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          {saveStatus === 'saving' && (
            <>
              <span>⏳</span>
              <span>Saving settings...</span>
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <span>✅</span>
              <span>Settings saved</span>
            </>
          )}
          {saveStatus === 'error' && (
            <>
              <span>❌</span>
              <span>Save failed: {saveError}</span>
            </>
          )}
        </div>
      )}
      {/* States Section - Collapsible */}
      <div style={styles.section}>
        <button
          onClick={() => setStatesExpanded(!statesExpanded)}
          style={styles.collapsibleHeader}
        >
          <span style={styles.chevron}>{statesExpanded ? '▼' : '▶'}</span>
          <span style={styles.sectionLabel}>States</span>
          <span style={styles.badge}>{stateCount === 0 ? 'all' : `${stateCount}`}</span>
        </button>

        {statesExpanded && (
          <div style={styles.collapsibleContent}>
            <div style={styles.stateGrid}>
              {VALID_STATES.map(state => (
                <label key={state} style={styles.stateCheckbox}>
                  <input
                    type="checkbox"
                    checked={(rules.states_include || []).includes(state)}
                    onChange={() => handleStateToggle(state)}
                    style={styles.checkbox}
                  />
                  <span style={styles.stateLabel}>{state}</span>
                </label>
              ))}
            </div>
            <div style={styles.buttonGroup}>
              <button onClick={handleSelectAllStates} style={styles.linkButton}>
                Select All
              </button>
              <button onClick={handleClearAllStates} style={styles.linkButton}>
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Addenda Section - Collapsible */}
      <div style={styles.section}>
        <button
          onClick={() => setAddendaExpanded(!addendaExpanded)}
          style={styles.collapsibleHeader}
        >
          <span style={styles.chevron}>{addendaExpanded ? '▼' : '▶'}</span>
          <span style={styles.sectionLabel}>Addenda</span>
        </button>

        {addendaExpanded && (
          <div style={styles.collapsibleContent}>
            {ADDENDUM_OPTIONS.map(option => (
              <label key={option.key} style={styles.addendumCheckbox}>
                <input
                  type="checkbox"
                  checked={rules[option.key] !== false}
                  onChange={() => handleAddendumToggle(option.key)}
                  style={styles.checkbox}
                />
                <span style={styles.addendumLabel}>{option.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Package Order Section - Collapsible */}
      <div style={styles.section}>
        <button
          onClick={() => setPackageOrderExpanded(!packageOrderExpanded)}
          style={styles.collapsibleHeader}
        >
          <span style={styles.chevron}>{packageOrderExpanded ? '▼' : '▶'}</span>
          <span style={styles.sectionLabel}>Package Order</span>
        </button>

        {packageOrderExpanded && (
          <div style={styles.collapsibleContent}>
            <div style={styles.packageOrderContainer}>
              {packageOrder.map(item => (
                <div
                  key={item}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, item)}
                  style={{
                    ...styles.packageOrderItem,
                    opacity: draggedItem === item ? 0.5 : 1,
                  }}
                >
                  <span style={styles.dragHandle}>⋮</span>
                  <span style={styles.packageItemLabel}>{formatDocLabel(item)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Early Pay - Minimal UI
 */
function EarlyPayRules({ templateId, versionId, token, authoring, onRulesSaved }) {
  const [rules, setRules] = useState({});
  const autoSaveRef = useRef(null);

  useEffect(() => {
    if (authoring?.applicability_rules_json) {
      setRules(authoring.applicability_rules_json);
    }
  }, [authoring]);

  const autoSave = useCallback((updatedRules) => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(async () => {
      try {
        await axios.patch(
          `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
          { applicability_rules_json: updatedRules },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (onRulesSaved) {
          onRulesSaved(updatedRules);
        }
      } catch (e) {
        console.error('Auto-save failed:', e);
      }
    }, 400);
  }, [templateId, versionId, token, onRulesSaved]);

  const handleChange = (value) => {
    const newRules = { ...rules, early_pay_options: parseInt(value, 10) };
    setRules(newRules);
    autoSave(newRules);
  };

  const value = rules.early_pay_options || 1;

  return (
    <div style={styles.container}>
      <div style={styles.section}>
        <label style={styles.minimalLabel}>
          Early Pay Options
        </label>
        <select
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          style={styles.select}
        >
          {[1, 2, 3, 4, 5, 6].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/**
 * Disclosure - Minimal UI
 */
function DisclosureRules({ templateId, versionId, token, authoring, onRulesSaved }) {
  const [rules, setRules] = useState({});
  const autoSaveRef = useRef(null);

  useEffect(() => {
    if (authoring?.applicability_rules_json) {
      setRules(authoring.applicability_rules_json);
    }
  }, [authoring]);

  const autoSave = useCallback((updatedRules) => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(async () => {
      try {
        await axios.patch(
          `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
          { applicability_rules_json: updatedRules },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (onRulesSaved) {
          onRulesSaved(updatedRules);
        }
      } catch (e) {
        console.error('Auto-save failed:', e);
      }
    }, 400);
  }, [templateId, versionId, token, onRulesSaved]);

  const handleChange = (value) => {
    const newRules = { ...rules, assigned_state: value };
    setRules(newRules);
    autoSave(newRules);
  };

  const value = rules.assigned_state || '';

  return (
    <div style={styles.container}>
      <div style={styles.section}>
        <label style={styles.minimalLabel}>
          State
        </label>
        <select
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          style={styles.select}
        >
          <option value="">Select a state...</option>
          {VALID_STATES.map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/**
 * Helper: Format document label
 */
function formatDocLabel(docType) {
  const labels = {
    'main_contract': 'Main Contract',
    'state_disclosure': 'State Disclosure',
    'weekly_addendum': 'Weekly Addendum',
    'early_pay_addendum': 'Early Pay Addendum',
    'refi_third_party_payoff_page': 'Refi / Payoff',
    'additional_entity_addendum': 'Additional Entity',
    'misc': 'Misc Addenda',
  };
  return labels[docType] || docType;
}

/**
 * Styles - Flat, minimal, integrated
 */
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.MD,
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.SM,
  },

  sectionLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: COLORS.TEXT_PRIMARY,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  minimalLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: COLORS.TEXT_PRIMARY,
    marginBottom: '4px',
    display: 'block',
  },

  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.SM,
    padding: `${SPACING.XS} 0`,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    color: COLORS.TEXT_PRIMARY,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'color 0.2s',
  },

  chevron: {
    fontSize: '10px',
    color: COLORS.TEXT_LIGHT,
    width: '12px',
    display: 'inline-block',
  },

  badge: {
    marginLeft: 'auto',
    fontSize: '11px',
    color: COLORS.TEXT_LIGHT,
    fontWeight: '500',
  },

  collapsibleContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.SM,
    paddingLeft: SPACING.SM,
  },

  stateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '6px',
  },

  stateCheckbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    fontSize: '11px',
  },

  stateLabel: {
    color: COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },

  checkbox: {
    cursor: 'pointer',
    accentColor: COLORS.PRIMARY_DARK,
    width: '14px',
    height: '14px',
  },

  buttonGroup: {
    display: 'flex',
    gap: SPACING.SM,
    marginTop: SPACING.XS,
  },

  linkButton: {
    background: 'none',
    border: 'none',
    color: COLORS.PRIMARY_DARK,
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '0',
    textDecoration: 'underline',
  },

  addendumCheckbox: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.SM,
    cursor: 'pointer',
    fontSize: '12px',
    color: COLORS.TEXT_PRIMARY,
  },

  addendumLabel: {
    fontWeight: '500',
  },

  packageOrderContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },

  packageOrderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.SM,
    padding: `${SPACING.XS} ${SPACING.SM}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    borderRadius: RADIUS.DEFAULT,
    fontSize: '12px',
    color: COLORS.TEXT_PRIMARY,
    cursor: 'move',
    transition: 'all 0.2s',
    border: '1px solid transparent',
  },

  dragHandle: {
    color: COLORS.TEXT_LIGHT,
    fontWeight: '600',
    fontSize: '12px',
    cursor: 'grab',
  },

  packageItemLabel: {
    fontWeight: '500',
    flex: 1,
  },

  select: {
    width: '100%',
    padding: `${SPACING.XS} ${SPACING.SM}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: '12px',
    color: COLORS.TEXT_PRIMARY,
    backgroundColor: COLORS.BG_WHITE,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
};
